import React from "react";

import DefaultLayout from "../layouts/DefaultLayout";
import ProjectSummary from "../sections/Project/ProjectSummary";
import ProjectGallery from "../sections/Project/ProjectGallery";
import ProjectSecondaryBanner from "../sections/Project/ProjectSecondaryBanner";
import ProjectTextBlock from "../sections/Project/ProjectTextBlock";
import ProjectFullWidthBanner from "../sections/Project/ProjectFullWidthBanner";
import ProjectSmallTextBlocks from "../sections/Project/ProjectSmallTextBlocks";
import ProjectTextInfo from "../sections/Project/ProjectTextInfo";
import ProjectBanner from "../sections/Project/ProjectBanner";
import ProjectFeature from "../sections/Project/ProjectFeature";
import ProjectMeta from "../sections/Project/ProjectMeta";
import ProductsSlider from "../sections/ProductsSlider/ProductsSlider";
import DefaultHelmet from "../seo/DefaultHelmet";
import beaconWidgetIds from "../settings/beacon-widget-ids";
import useBeaconWidget from "../hooks/useBeaconWidget";
import PageHeader from "../components/PageHeader";
import useSchemaSettings from "../seo/schema/useSchemaSettings";
import { buildThemePageSchema } from "../seo/schema";

const Theme = ({ pageContext }) => {
	const { theme, productsSliderItems } = pageContext;

	const getSection = ({ fieldGroupName, ...section }, index) => {
		const key = index;

		switch (fieldGroupName) {
			case 'shopifyTheme_Acftheme_Sections_ThemeSummary': {
				return (
					<ProjectSummary
						key={key}
						theme
						withManual
						technologies={theme.technologies?.nodes}
						{...section}
					/>
				);
			}

			case 'shopifyTheme_Acftheme_Sections_ThemeGallery': {
				return (
					<ProjectGallery
						key={key}
						projectTitle={theme.title}
						{...section}
					/>
				);
			}

			case 'shopifyTheme_Acftheme_Sections_ThemeSecondaryBanner': {
				return (
					<ProjectSecondaryBanner
						key={key}
						projectTitle={theme.title}
						{...section}
					/>
				);
			}

			case 'shopifyTheme_Acftheme_Sections_ThemeSingleTextBlock': {
				return (
					<ProjectTextBlock
						key={key}
						titleTag={section.blockAlignment === "left" ? "h3" : "h2"}
						{...section}
					/>
				);
			}

			case 'shopifyTheme_Acftheme_Sections_ThemeFullWidthBanner': {
				return (
					<ProjectFullWidthBanner
						key={key}
						{...section}
					/>
				);
			}

			case 'shopifyTheme_Acftheme_Sections_ThemeSmallTextBlocks': {
				return (
					<ProjectSmallTextBlocks
						key={key}
						itemTitleTag="p"
						{...section}
					/>
				);
			}

			case 'shopifyTheme_Acftheme_Sections_ThemeTextInfo': {
				return (
					<ProjectTextInfo
						key={key}
						titleTag="h3"
						{...section}
					/>
				);
			}

			case 'shopifyTheme_Acftheme_Sections_ThemeBanner': {
				const headerOnBanner = theme.page_header?.position === 'on';
				const headerBelowBanner = theme.page_header?.position === 'below' || theme.page_header?.position === 'bellow';

				const titleBelowBannerMarkup = (
					headerBelowBanner &&
					theme.page_header?.title
				)
					? <PageHeader {...theme.page_header} isGeneral={true} />
					: null;

				return (
					<React.Fragment key={key}>
						<ProjectBanner
							projectTitle={theme.title}
							pageHeader={headerOnBanner ? theme.page_header : null}
							{...section}
						/>

						<ProjectMeta type={"shopify-themes"}/>

						{headerBelowBanner && titleBelowBannerMarkup}
					</React.Fragment>

				);
			}

			case 'shopifyTheme_Acftheme_Sections_ThemeFeature': {
				return (
					<ProjectFeature
						key={key}
						projectTitle={theme.title}
						titleTag="h3"
						{...section}
					/>
				);
			}

			default: {
				return null;
			}
		}
	};

	const getSections = () => {
		if(!Array.isArray(theme.acfTheme?.sections)) {
			return null;
		}
		return theme.acfTheme.sections.map(getSection);
	}

	const pageTitle = theme?.acfTheme?.metaData?.title || "Theme";
	const pageDescription = theme?.acfTheme?.metaData?.description || "Shopify theme description";
	const pageKeywords = "Shopify theme, UTD, customizable theme, premium theme, mobile-first theme, e-commerce theme, ADA compliant, cross-browser compatibility, cross-platform compatibility, conversion optimization, sales boosting, unique features, customization options."
	const schemaContext = useSchemaSettings();
	const structuredData = buildThemePageSchema({
		schemaContext,
		theme,
		title: pageTitle,
		description: pageDescription,
	});

	useBeaconWidget(beaconWidgetIds.THEME);

	return (
		<DefaultLayout preFooterSections={<ProductsSlider items={productsSliderItems} />}>
			<DefaultHelmet title={pageTitle} description={pageDescription} keywords={pageKeywords} structuredData={structuredData}/>

			{getSections()}
		</DefaultLayout>
	)
};

export default Theme;
